import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, Checkbox, CircularProgress,
  Box
} from '@mui/material';
import { Column } from './interfaces';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

interface DataTableProps<T> {
  columns: Column[];
  fetchData: (query?: { pageSize?: number; nextPageToken?: string } | { page?: number; limit?: number }) => Promise<T>;
  dataExtractor: (response: T) => any[];
  nextPageTokenExtractor?: (response: T) => string | undefined;
  orientation?: 'default' | 'transposed'; // New prop for orientation
  size?: 'medium' | 'small'; // New prop for compact mode
  selectable?: boolean;
  getRowColor?: (row: any) => string; // New prop for row color
  expandable?: boolean; // New prop to control if rows are expandable
  renderExpandedContent?: (row: any) => React.ReactNode;
}

const DataTable = <T,>({ columns, fetchData, dataExtractor, nextPageTokenExtractor, orientation = 'default', size = 'medium', selectable = false, getRowColor, expandable = false, renderExpandedContent }: DataTableProps<T>) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected] = useState<string[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>();
  const [openRows, setOpenRows] = useState<{ [key: string]: boolean }>({}); // State to track the expanded rows


  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        // const result = await fetchData({
        //   pageSize: rowsPerPage,
        //   nextPageToken: page > 0 ? nextPageToken : undefined
        // });
        const queryParams = nextPageTokenExtractor
          ? {
            pageSize: rowsPerPage,
            nextPageToken: page > 0 ? nextPageToken : undefined,
          }
          : {
            page: page + 1, // assuming page is 1-based in the API
            limit: rowsPerPage,
          };
        const result = await fetchData(queryParams);
        const extractedData = dataExtractor(result);
        const extractedNextPageToken = nextPageTokenExtractor ? nextPageTokenExtractor(result) : undefined;
        setData(extractedData);
        setNextPageToken(extractedNextPageToken);
      } catch (error) {
        console.error('Failed to fetch data', error);
        setData([]);  // Reset data to an empty array on error
      }
      setLoading(false);
    };

    getData();
  }, [fetchData, page, rowsPerPage, dataExtractor, nextPageTokenExtractor]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    // setPage(newPage);
    if (nextPageTokenExtractor) {
      setPage(newPage);
    } else if (newPage >= 0) {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRow = (id: string) => {
    setOpenRows(prevState => ({ ...prevState, [id]: !prevState[id] })); // Toggle row expansion
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  const formatCellValue = (value: any, type?: string) => {
    if (type === 'numeric') {
      return new Intl.NumberFormat().format(value); // Locale-aware numeric formatting
    }
    if (type === 'datetime') {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(new Date(value)); // Locale-aware datetime formatting
    }
    return value; // Default: no formatting
  };

  return (
    <Paper>
      <TableContainer>
        <Table size={size}>
          {orientation === 'default' ? (
            <>
              <TableHead>
                <TableRow>
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < data.length}
                        checked={data.length > 0 && selected.length === data.length}
                        onChange={handleSelectAllClick}
                        inputProps={{ 'aria-label': 'select all items' }}
                      />
                    </TableCell>
                  )}

                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.type === 'numeric' ? 'right' : 'left'}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <React.Fragment key={row.id || index}>
                        <TableRow
                          hover
                          onClick={(event) => handleClick(event, row.id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.id || index}
                          selected={isItemSelected}
                          sx={{ backgroundColor: getRowColor ? getRowColor(row) : 'inherit' }}
                        >
                          {selectable && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                inputProps={{ 'aria-labelledby': labelId }}
                              />
                            </TableCell>
                          )}
                          {expandable && (
                            <TableCell padding="checkbox">
                              <IconButton aria-label="expand row" size="small" onClick={() => toggleRow(row.id)}>
                                {openRows[row.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </TableCell>
                          )}
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.type === 'numeric' ? 'right' : 'left'}
                              onClick={(event) => column.onCellClick && column.onCellClick(row[column.id], row, event)}
                              sx={column.onCellClick ? {
                                cursor: 'pointer',
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              } : {}}
                            >
                              {column.render ? column.render(row[column.id], row) : formatCellValue(row[column.id], column.type)}
                            </TableCell>
                          ))}
                        </TableRow>

                        {/* Expanded content */}
                        {expandable && renderExpandedContent && (
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length + (selectable ? 2 : 1)}>
                              <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 1 }}>
                                  {renderExpandedContent(row)}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </>
          ) : (
            <>
              <TableBody>
                {columns.map((column) => {
                  // Check if the column has at least one non-null/undefined value
                  const hasValue = data.some(row => row[column.id] !== null && row[column.id] !== undefined && row[column.id] !== "");

                  if (!hasValue) {
                    return null; // Skip rendering this column if no values are present
                  }

                  return (
                    <TableRow key={column.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap', width: '1px' }}>{column.label}</TableCell>
                      {data.map((row, index) => (
                        <TableCell key={index}>
                          {column.render ? column.render(row[column.id], row) : formatCellValue(row[column.id], column.type)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={-1} // Assuming count is unknown, which is typical for token-based pagination
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        slotProps={{
          actions: {
            nextButton: {
              disabled: nextPageTokenExtractor
                ? !nextPageToken || nextPageToken.startsWith('/')
                : false, // Disable only if using nextPageToken and the logic applies
            },
          },
        }}
      />

    </Paper>
  );
};

export default DataTable;
